/**
 * OPEN WATER. POKOMOKO governs it (CONTRACT 1.5g(a)); Eastward keeps the shoreline (contact.ts).
 *
 * The spec is gauntlet/living-sea/OPEN-WATER.md section 4, measured off the lossless CC0 asset
 * and converted to the sea's grain-2 scale. The tile itself is baked from that asset by
 * tools/world-render/bake-water.mjs into src/render/baked/water.png, with every section-4.1
 * figure measured on the output in water.json. This file carries what the runtime needs beyond
 * the pixels: WHEN the loop advances, WHICH frame a tile shows, and THE FIELD - what the tile
 * is multiplied by across the sea.
 *
 * -- WHAT THE ASSET FIXES (MEASURED, DERIVED) ----------------------------------------------
 *
 *   A 48x48 art px tile, seamless both axes, four values, one crest line per tile that crosses
 *   every row and every column exactly once, a looser mid-tone line, sixteen frames that close
 *   into a loop. Two layers move against each other: the crest oscillates up-right along
 *   dx = -dy and back (net zero over the loop); the mid tone scrolls down-left at (-3, +3) px
 *   per frame and wraps exactly once. 30.2% of the surface changes value every frame.
 *
 * -- WHAT THE ASSET DOES NOT FIX, and what this file rules instead ---------------------------
 *
 *   FRAME RATE. The PNG carries no timing chunk. WATER_FRAME_MS is section 4.3's MODELLED
 *   figure and the one constant to tune by eye; `?wave=<ms>` on world.html overrides it for a
 *   look without a rebuild.
 *
 *   THE FIELD, and this is round 5's whole subject. The owner watched round 4 - twenty tiles
 *   per row, every one at full amplitude in lockstep, 30% of the water flipping by ~105 luma
 *   every 190 ms in one regular diagonal - and rejected it: "the waves currently hurt my eyes
 *   how they are on the screen. It's dizzying." CONTRACT 1.5i(d) names the cause: POKOMOKO
 *   governs the PIXEL and round 4 applied it to the FIELD, the oldest structural failure in
 *   this project's ledger ("one richer treatment applied everywhere"). The ruling is that the
 *   open-water field must have CALM: amplitude and churn fall away from shore and from the
 *   boat, alive where it means something and quiet where the eye rests. The tile is untouched;
 *   what it is multiplied by is a design decision the owner judges by eye, so every constant
 *   in WATER_CALM is a GUESS, exposed as a query parameter on world.html.
 *
 *   HOW THE MULTIPLY IS DONE. Amplitude is evaluated per WATER_CELL_PX cell (a third of a
 *   tile) and quantised to WATER_LEVELS steps; the renderer holds one recoloured copy of the
 *   sheet per level, the three deltas scaled by the level, and each cell draws its slice of
 *   the copy for its level. Every cell is therefore still exactly four hard values with no
 *   gradient inside it, and a step between neighbouring levels is 163.5 / 31 = 5 luma on the
 *   crest, below anything the eye separates on a moving line. This is the honest pixel-art
 *   way to multiply a tile: a shader lerp would put a gradient along the crest.
 *
 *   THE REPEAT. Round 4 tried a per-tile hashed phase ('hash') and rightly rejected it as a
 *   mosaic: the tile is seamless only with itself on the same frame, so random neighbours jog
 *   by up to 17 px at every seam (gauntlet/spike/r4/crop-field-hash-vs-uniform.png). Round 5
 *   tries the two things that are not that. 'swell' is a SMOOTH phase field over hundreds of
 *   pixels - neighbours differ by at most one frame, a 3 px jog - so the crest lines bend
 *   gently instead of ruling one straight diagonal. 'cross' lays a second copy at the opposite
 *   phase over the first, faded in and out by a slow field so no single crest line dominates;
 *   it is the one field here that blends colours, and is kept for the comparison. The default
 *   is whichever read as sea in gauntlet/spike/r5/; see WATER_FIELD.
 *
 * -- PRESENCE CHANGES NOTHING -----------------------------------------------------------------
 *
 *   The global frame is floor(nowMs / WATER_FRAME_MS) mod 16 on the wall-clock epoch, and the
 *   whole field - amplitude, gusts, phase - is a function of world coordinates and of the
 *   INSTANT THE CURRENT WAVE FRAME BEGAN (`waterTickAt`), never of when the host happened to
 *   wake. Nothing here counts draws or holds state, so a tab drawing at 4 Hz and one at 120 Hz
 *   show the identical sea at the identical instant. water.test.ts is the fence, on the same
 *   four draw-rate grids as interpolate.test.ts, and it covers the calm field too.
 */

import { hash32, unit } from './hash';
import type { Px } from './scale';

/** DERIVED: POKOMOKO's 32 px tile at x1.5 (OPEN-WATER.md section 4.1). */
export const WATER_TILE_PX = 48;
/** MEASURED: sixteen frames, and the loop closes (section 3.1). */
export const WATER_LOOP_FRAMES = 16;
/**
 * MODELLED (section 4.3): the mid tone travels 3 art px per frame, 0.588 m at the sea's scale;
 * deep-water dispersion at the tile's 6.15 m wavelength gives 3.10 m/s; 0.588 / 3.10 = 0.190 s.
 * The asset carries no timing at all. THE ONE CONSTANT TO TUNE BY EYE.
 */
export const WATER_FRAME_MS = 190;

/**
 * GUESS: the cell the amplitude is quantised over. A third of a tile, so the falloff has
 * three steps per tile rather than one; 48 would make each tile a single brightness and show
 * the tiling as rings. Must divide WATER_TILE_PX.
 */
export const WATER_CELL_PX = 16;
/** GUESS: amplitude steps. 32 puts a 5-luma step between neighbours on the crest. */
export const WATER_LEVELS = 32;

export type WaterField = 'uniform' | 'hash' | 'swell' | 'cross';
/**
 * GUESS, judged by eye against gauntlet/spike/r5/: 'uniform' is the spec-exact tiling and the
 * calm field alone is what breaks its lockstep; 'swell' and 'cross' are the round-5 trials
 * against the regular diagonal and 'hash' is round 4's rejected mosaic, all behind `?field=`.
 */
export const WATER_FIELD: WaterField = 'uniform';

/**
 * THE CALM. Every figure is a GUESS the owner tunes by eye (CONTRACT 1.5i(d)); world.html
 * exposes each as a query parameter of the same name.
 */
export interface WaterCalm {
  /** Amplitude of the open sea far from any shore and from the boat, 0..1. 1 is round 4. */
  readonly floor: number;
  /** World art px over which amplitude falls from 1 at the boat to the floor. */
  readonly radius: number;
  /**
   * The same, from a shoreline. Separate from the boat's because an islet-dense frame has a
   * shore within a boat-radius of nearly all its water, and one shared radius lifted the whole
   * frame back to round 4 (gauntlet/spike/r5/r5-shore220-five-islets-1920x1080.png).
   */
  readonly shoreRadius: number;
  /** How far the slow gust field can lift the open sea above the floor, 0..1. 0 is a flat floor. */
  readonly gust: number;
  /** The gust field's lattice spacing in world art px: how big a patch is. */
  readonly gustPx: number;
  /** How fast a gust patch drifts, world art px per second, and (a fifth of that) how fast it evolves. */
  readonly gustDrift: number;
  /** Tiles whose amplitude is below this hold every other frame, halving their churn. 0 disables. */
  readonly hold: number;
}

export const WATER_CALM: WaterCalm = {
  floor: 0.2, // GUESS: crest at +33 luma instead of +163; the far sea is present, not busy. 0.3 was tried and still read as line work (gauntlet/spike/r5/crop-ab-far-default-vs-calmlow-vs-swell-3x.png)
  radius: 220, // GUESS: the full-amplitude disc round the boat is 46% of the frame's width
  shoreRadius: 90, // GUESS: just under two tiles of live water off a shore
  gust: 0.25, // GUESS: open sea breathes between 0.20 and 0.45
  gustPx: 256, // GUESS: patches a little over five tiles across
  gustDrift: 12, // GUESS: a patch crosses the frame in 80 s
  hold: 0, // GUESS, and off: the half-rate hold read as a stutter, not as calm (see the r5 crops)
};

/** The frame the loop is on at `nowMs`: a pure function of the instant. 0..15. */
export function waterFrameAt(nowMs: number, frameMs: number = WATER_FRAME_MS): number {
  const f = Math.floor(nowMs / frameMs) % WATER_LOOP_FRAMES;
  return f < 0 ? f + WATER_LOOP_FRAMES : f;
}

/**
 * The instant the wave frame that contains `nowMs` began. The field is sampled HERE and not at
 * `nowMs`, so two hosts that wake at different moments inside the same frame - a 4 Hz tab and a
 * 500 Hz tab, or one that woke 6 ms late - evaluate the identical field.
 */
export function waterTickAt(nowMs: number, frameMs: number = WATER_FRAME_MS): number {
  return Math.floor(nowMs / frameMs) * frameMs;
}

/** Real milliseconds until the loop next advances: in (0, frameMs]. The host sleeps this long. */
export function msUntilNextWaterFrame(nowMs: number, frameMs: number = WATER_FRAME_MS): number {
  const into = ((nowMs % frameMs) + frameMs) % frameMs;
  return frameMs - into;
}

// ---- noise: the one smooth field everything slow is built on ----------------------------------

const smooth = (t: number) => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Value noise on an integer lattice, smoothstep-interpolated, in [0, 1]. The corners come from
 * hash32 of the lattice coordinate and a salt - no seed, no state - so it is the same field in
 * every tab. Stretched x2 about 0.5 and clamped, because trilinear blending of eight uniforms
 * has a quarter of their contrast and the patches would otherwise all be middling.
 */
export function valueNoise3(u: number, v: number, w: number, salt: number): number {
  const iu = Math.floor(u), iv = Math.floor(v), iw = Math.floor(w);
  const fu = smooth(u - iu), fv = smooth(v - iv), fw = smooth(w - iw);
  const c = (a: number, b: number, d: number) => unit(hash32(iu + a, iv + b, (iw + d) * 0x10001 + salt, 0x5ea));
  const y0 = lerp(lerp(c(0, 0, 0), c(1, 0, 0), fu), lerp(c(0, 1, 0), c(1, 1, 0), fu), fv);
  const y1 = lerp(lerp(c(0, 0, 1), c(1, 0, 1), fu), lerp(c(0, 1, 1), c(1, 1, 1), fu), fv);
  const n = lerp(y0, y1, fw);
  return Math.min(1, Math.max(0, (n - 0.5) * 2 + 0.5));
}

const SALT_GUST = 1;
const SALT_SWELL = 2;
const SALT_CROSS = 3;

/**
 * The gust field at a world point and a wave-frame tick, 0..1: slow patches that drift across
 * the sea and evolve as they go, so the open water breathes instead of holding one brightness.
 */
export function gustAt(wx: number, wy: number, tickMs: number, calm: WaterCalm = WATER_CALM): number {
  const s = tickMs / 1000;
  const L = calm.gustPx;
  return valueNoise3((wx - calm.gustDrift * s) / L, (wy - calm.gustDrift * 0.4 * s) / L, (calm.gustDrift * 0.2 * s) / L, SALT_GUST);
}

/**
 * A thing with a shoreline: its centre in world art px, and the half-extents of the sprite it
 * stands for. The shape used is a STADIUM - the box shrunk to its long axis and given a corner
 * radius of its short half-extent - which is a disc when the sprite is square and a capsule when
 * it is long.
 *
 * ROUND 4 USED A DISC of the sprite's mean half-extent, and that was fine while every island was
 * a 203 x 195 px islet. CONTRACT 1.5h(b) then made an island 1,560-3,120 px across and 562 px
 * up-screen, and the disc of a 2,483 x 562 sprite has a 761 px radius: MEASURED on 1,260 water
 * cells of a 960 x 540 frame at the island, that disc puts 78.9% of them at maximum amplitude.
 * That is the lockstep field CONTRACT 1.5i(d) rejected, reintroduced by geometry alone. Under the
 * stadium the same frame is 0.3%.
 *
 * THE STADIUM AND NOT THE PLAIN BOX, and the difference is measured rather than argued. A plain
 * box reaches sqrt(2) times further at its corners than a disc does, which for a 203 x 195 islet
 * is 41 px of extra shore against a 90 px falloff: MEASURED, it moves 11.2% of the water cells
 * round a generated islet by up to 16 of the 32 amplitude levels. The sea round a generated islet
 * is a surface the owner is judging and this round has no business moving it. The stadium is also
 * the better model, because the corner of a sprite's bounding box is water and not land.
 *
 * WHAT THE STADIUM STILL MOVES, all of it MEASURED the same way (levels, not amplitudes, over the
 * water cells of a 960 x 540 frame with the islet a third of a frame off the boat):
 *
 *   settled-5000  203 x 195   4.6% of cells move, largest step 1 of 32
 *   settled-5001  203 x 192   5.8%, largest step 2
 *   wooded-5000   187 x 209   7.8%, largest step 3
 *   isle-4007     140 x 296  13.0%, largest step 15 - and its cells at MAXIMUM amplitude fall
 *                            from 2.6% to 0.9%. It is the one generated sprite far from square,
 *                            its round-4 disc over-reached by 39 px, and the correction runs the
 *                            same way CONTRACT 1.5i(d) does: less of the field in lockstep.
 */
export interface Shore {
  readonly x: number;
  readonly y: number;
  /** Half the sprite's width and height, art px. */
  readonly hw: number;
  readonly hh: number;
}

/** Distance from a point to a shore's stadium. 0 inside it. */
export function shoreDistance(wx: number, wy: number, s: Shore): number {
  const r = Math.min(s.hw, s.hh);
  const dx = Math.max(0, Math.abs(wx - s.x) - (s.hw - r));
  const dy = Math.max(0, Math.abs(wy - s.y) - (s.hh - r));
  return Math.max(0, Math.hypot(dx, dy) - r);
}

/** What the calm field is computed against for one frame: the boat and the shores in view. */
export interface CalmScene {
  readonly boat: Px;
  readonly shores: readonly Shore[];
}

/**
 * How far (wx, wy) is from anything alive, as a fraction of the falloff: 0 at the boat or inside
 * a shore's box, 1 at or beyond a boat-radius from the boat and a shore-radius from every shore.
 *
 * The distance to a shore is `shoreDistance`: the distance to the stadium that stands for the
 * sprite, which for a square sprite is within 2 px of the round-4 disc everywhere and for a long
 * one is a capsule rather than a bubble the size of the diagonal.
 */
export function lifeDistance(wx: number, wy: number, scene: CalmScene, calm: WaterCalm = WATER_CALM): number {
  let u = Math.hypot(wx - scene.boat.x, wy - scene.boat.y) / calm.radius;
  for (const s of scene.shores) {
    const us = shoreDistance(wx, wy, s) / calm.shoreRadius;
    if (us < u) u = us;
  }
  return Math.min(1, Math.max(0, u));
}

/**
 * THE AMPLITUDE the tile is multiplied by at a world point, 0..1. 1 at the boat and at a shore,
 * falling by a smoothstep over `radius` (`shoreRadius` off a shore) to the open-sea level,
 * which is the floor lifted by the gust field. Pure in (wx, wy, tickMs, scene, calm).
 */
export function calmAmplitude(wx: number, wy: number, tickMs: number, scene: CalmScene, calm: WaterCalm = WATER_CALM): number {
  const open = Math.min(1, calm.floor + (calm.gust > 0 ? calm.gust * gustAt(wx, wy, tickMs, calm) : 0));
  if (open >= 1) return 1;
  const u = lifeDistance(wx, wy, scene, calm);
  if (u >= 1) return open;
  return open + (1 - open) * (1 - smooth(u));
}

/**
 * HOW FAR OFF SCREEN A SPRITE'S BOX MAY BE AND STILL LIFT THE WATER A PLAYER CAN SEE.
 *
 * -- WHY THIS CONSTANT EXISTS, AND WHAT IT COST TO LEARN -----------------------------------------
 *
 * Round 9's WP0 culled the shore set to the frame, on the ground that a sprite nobody could see
 * was lifting the water at the edge it sat behind. The cull it shipped was a BARE BOX-OVERLAP
 * TEST, and a shore's influence does not stop at its box: it reaches `shoreRadius` past it.
 * MEASURED by a critic on the shipped tree, along a transect running in from the frame edge on
 * the draw a sprite's box crosses it: amplitude 0.285 -> 1.000 at the edge, level 9 -> 31 of 32,
 * over an ~80 art px band, in ONE draw, on BOTH arms. CONTRACT 1.5i(d) ("the waves currently hurt
 * my eyes... It's dizzying") is the ruling that class of flip already lost under.
 *
 * A cull is only invisible when what it drops could not have drawn anything. So the test is the
 * box GROWN by the field's own reach, and the margin is that reach:
 *
 *   shoreRadius   90  the distance past a shore's stadium at which `lifeDistance` returns 1 and
 *                     the lift is exactly the open-sea floor - i.e. nothing.
 *   WATER_CELL_PX 16  a water cell that is partly on screen is sampled at its CENTRE, which can
 *                     sit outside the frame; the margin has to cover the sample, not the pixel.
 *
 * ISLET_MARGIN_LEAGUES is 6 x 8 leagues, which is 240 x 277 art px, so a 106 px margin still
 * drops the sprites WP0 was about and no longer truncates a field inside its own reach.
 */
export const SHORE_CULL_MARGIN_PX = WATER_CALM.shoreRadius + WATER_CELL_PX;

/**
 * Does a sprite's box, at SCREEN top-left (ax, ay), come within `marginPx` of the frame?
 *
 * One predicate, exported, because the renderer and `tools/lanes/surfaces/arrival.mjs` have to
 * agree about it exactly: the instrument that gates 1.5q(b) models the renderer's shore set, and
 * the two drifting apart is how the shipped cull went unmeasured in the first place.
 */
export function boxWithinFrame(ax: number, ay: number, w: number, h: number, artW: number, artH: number, marginPx: number): boolean {
  return !(ax + w + marginPx <= 0 || ay + h + marginPx <= 0 || ax - marginPx >= artW || ay - marginPx >= artH);
}

/** The amplitude level a cell draws at: 0..WATER_LEVELS-1, monotone in the amplitude. */
export function levelOf(amplitude: number): number {
  return Math.round(Math.min(1, Math.max(0, amplitude)) * (WATER_LEVELS - 1));
}

/** The frame a held tile shows for loop frame `f`: the same picture for two frames running. */
export function heldFrame(f: number): number {
  return f - (f % 2);
}

/**
 * GUESS: the swell field's lattice and depth. Over 400 px the phase moves by at most three
 * frames, so neighbouring tiles differ by at most one - a 3 px jog at the seam, against the
 * hashed field's seventeen.
 */
export const SWELL_PX = 400;
export const SWELL_FRAMES = 3;

/**
 * Where tile (tx, ty) starts in the loop. 'uniform' and 'cross': everywhere at once. 'hash':
 * a phase from the tile's world coordinate alone. 'swell': a phase from the smooth field at
 * the tile's centre. No seed, no time, so it is the same in every tab and on every reload.
 *
 * THE COST of 'hash', named: the tile is seamless only with itself on the same frame. With
 * neighbours on different frames the crest line jogs at a seam by the difference of the two
 * frames' offsets (up to 17 px along the diagonal) and the mid line by 3 px per frame of
 * difference. Judged in round 4: it reads as a broken tiling, not as chop (see the header).
 */
export function tilePhase(tx: number, ty: number, field: WaterField = WATER_FIELD): number {
  if (field === 'uniform' || field === 'cross') return 0;
  if (field === 'hash') return hash32(tx, ty, 0x7a7e, 1) % WATER_LOOP_FRAMES;
  const cx = (tx + 0.5) * WATER_TILE_PX;
  const cy = (ty + 0.5) * WATER_TILE_PX;
  return Math.round(SWELL_FRAMES * valueNoise3(cx / SWELL_PX, cy / SWELL_PX, 0, SALT_SWELL));
}

/** The frame tile (tx, ty) shows when the loop is on `globalFrame`. */
export function tileFrame(tx: number, ty: number, globalFrame: number, field: WaterField = WATER_FIELD): number {
  return (globalFrame + tilePhase(tx, ty, field)) % WATER_LOOP_FRAMES;
}

/** 'cross' only: the second layer's weight at a world point, 0..1, from a slow drifting field. */
export function crossWeightAt(wx: number, wy: number, tickMs: number, calm: WaterCalm = WATER_CALM): number {
  const s = tickMs / 1000;
  const L = calm.gustPx;
  return valueNoise3((wx + calm.gustDrift * 0.6 * s) / L, (wy - calm.gustDrift * 0.5 * s) / L, (calm.gustDrift * 0.15 * s) / L, SALT_CROSS);
}

/** The inclusive tile index range covering world art px [a, b). */
export function tileRange(a: number, b: number): readonly [number, number] {
  return [Math.floor(a / WATER_TILE_PX), Math.floor((b - 1) / WATER_TILE_PX)];
}

// ---- THE TONE SEA (round 9, CONTRACT 1.5w) ----------------------------------------------------

/**
 * WHICH SEA IS DRAWN, and it is one reload apart from the other.
 *
 * CONTRACT 1.5w: "i love the water in those renders if you could improve the reelin game to have
 * water more like that". That amends 1.5o(a) ("the sea is finished") and it does NOT strike
 * 1.5g(a), which rules POKOMOKO the governor of the open-water pixel. Under `?water=render` the
 * open-water pixel is no longer POKOMOKO's - it is a flat tone from a field frozen in world
 * space, with a marching-squares boundary and a marching-squares crest over it - and that is an
 * OWED RULING, named here rather than taken. The owner rules by CHOOSING AN ARM;
 * `?water=settled` is today's sea, unmoved, so the choice stays open.
 *
 * The settled arm is not refactored, moved or re-baked by this round: `water.png`, `water.json`,
 * `buildWaterLevels`, `classifyWaterSheet`, `calmAmplitude`, `shoreDistance`, `levelOf`, the
 * sixteen-frame loop, the frame key and `nextDelayMs` are byte-identical, which is why every
 * `it` in water.test.ts above is untouched.
 */
export type WaterMode = 'render' | 'settled';
/** RULING pending the owner's eye: the render arm is what a plain load shows. `?water=settled` is one reload away. */
export const WATER_MODE: WaterMode = 'render';

/**
 * THE TONE SEA'S CONSTANTS, and EVERY ONE IS A GUESS behind a query parameter of the same name.
 * Nothing here is measured off the renders: the renders fix what the sea should READ as, and
 * gauntlet/living-sea/render-target-20260910/measure.json is the row the likeness instrument
 * compares against. These are the knobs that get it there, and the owner tunes them by eye
 * exactly as they tune WATER_CALM.
 */
export interface WaterSea {
  /** How many tones the sea is quantised to, deepest first. `?tones`. 1.5e: hard values, no ramp. */
  readonly tones: number;
  /** World art px over which the shelf lightens the water towards a shore. `?shelf`. */
  readonly shelfPx: number;
  /** The open sea's own tone position, 0..1 of the ramp, before the mottle. `?deep`. */
  readonly deep: number;
  /** How far the mottle can move the tone position either way. `?mottle`. */
  readonly mottle: number;
  /** The mottle's lattice in world art px, before the 3:1 stretch along the swell diagonal. `?mottlepx`. */
  readonly mottlePx: number;
  /** The domain warp's first-octave lattice in world art px; the second is this over 2.45. `?warp` scales it. */
  readonly warpPx: number;
  /** How far the warp moves a sample, world art px. `?warp` sets this. */
  readonly warpAmp: number;
  /** Crest COVERAGE per unit of calm amplitude: what fraction of the sea the crest band reaches. `?crest`. */
  readonly crestGain: number;
  /** The crest field's lattice in world art px. `?crestpx`. */
  readonly crestPx: number;
  /**
   * THE CREST'S OWN REACH OFF A SHORE, world art px: past this the crest is the open sea's and
   * knows nothing about the land. `?crestshore`. CONTRACT 1.5y(1).
   */
  readonly crestShorePx: number;
  /**
   * What fraction of the calm's crest coverage survives in OPEN WATER, 0..1. 1 is the sea this
   * round inherited, where a cliff and six hundred metres out got the same density. `?crestopen`.
   */
  readonly crestOpen: number;
  /** The shore band's reach ACROSS the coast in world art px, before the along-shore stretch. `?crestbandpx`. */
  readonly crestBandPx: number;
  /** How far the shore band displaces the open field at a waterline, 0..1. `?crestband`. */
  readonly crestBandWeight: number;
  /**
   * What fraction of the crest's dash gate is open in OPEN WATER, 0..1, rising to sea.ts
   * CREST_DASH_ON at a waterline. `?crestdashopen`. See sea.ts `crestDashOnAt` for the measured
   * reason a gate and not only a threshold is needed here.
   */
  readonly crestDashOpen: number;
  /**
   * How much of the crest's LUMA LIFT is applied, x1 being today's +20 at calm and +50 at full
   * amplitude over the crest's own tone (palette.ts `crestFor`). `?crestlift`.
   *
   * THIS IS THE ONE MEASURED DIFFERENCE FROM THE RENDERS THE OWNER COULD NOT SEE A VERSION OF.
   * Every other constant here is a guess against a look; this one has a number on both sides.
   * MEASURED, `brightFractionPct` at `brightOverP50` 60 - the share of a rectangle's pixels more
   * than 60 luma above its own median: woolstrand's shelf 8.288, blackstage's 3.191, almadra's
   * 0.615 (gauntlet/living-sea/render-target-20260910/measure.json); the game's render arm 1.722
   * at Murrin Roost's shelf and 0.000 in its open water
   * (gauntlet/rounds/living-sea-r9/likeness-murrin-roost-render.json). The game is inside the
   * references' own spread and at the quiet end of it. Which end the owner wants is a look, so it
   * is a knob and not a correction: 1 is what shipped and nothing moves unless it is asked for.
   */
  readonly crestLift: number;
  /**
   * The crest stroke's width in art px, the pen `buildCrestBank` draws the contour with. Not
   * rounded: 2.9 is a real bank. `?creststroke`. See sea.ts CREST_STROKE_PX for the three
   * MEASURED reference medians (1.1, 2.0, 2.9) and for why the drawn thickness comes out above
   * the pen. Clamped to sea.ts CREST_STROKE_MAX_PX, which is the crest cell itself.
   */
  readonly crestStrokePx: number;
  /** Foam's widest reach at a sand shore, art px. Lip is 0.6 of it and rock is 0. `?foam`. */
  readonly foamPx: number;
}

export const WATER_SEA: WaterSea = {
  tones: 7, // GUESS: blackstage's open rectangle holds 2-3 tones and woolstrand's shelf 4-5; seven covers deep to beach with room for the shelf's own ramp
  shelfPx: 96, // GUESS, moved by eye from 184: at 184 Murrin Roost's whole bay was one pale tone. The renders' pale band is 60-150 render px, which is 30-75 art px
  deep: 0.18, // GUESS: the open sea sits on tone 1 of 7 and the mottle moves it between 0 and 2
  mottle: 0.52, // GUESS, moved by eye AND by the dull floor: at 0.30 the open-water rectangle held only 4 distinct values against sea-likeness.mjs's floor of 7, which is the pond this round risks
  mottlePx: 220, // GUESS: a blotch is about a fifth of a frame across before the 3:1 stretch
  warpPx: 311, // GUESS: boundaries bend over a third of a frame, not over a tile
  warpAmp: 40, // GUESS: 40 px of warp is what turns a smooth iso-line into an irregular one
  crestGain: 1.0, // GUESS: coverage = crestGain * calmAmplitude, so calm moves HOW MUCH crest there is
  crestPx: 40, // GUESS, moved by eye from 96: at 96 the crest was one iso-contour across the whole frame. See sea.ts crestDashAt
  crestShorePx: 100, // GUESS: the shore governs COMPLETELY out to sea.ts CREST_SHORE_HOLD of this (45 art px) and not at all past it. Close to the tone shelf's own 96, and the likeness instrument's open rectangles start 60 to 65 art px from land, so a longer reach measured its own band as open water
  // ROUND 10'S MARK SHAPE IS OFF, AND THESE THREE ARE WHAT TAKES IT OUT (living-sea r10 VERDICT,
  // its closing correction; CONTRACT 1.5y correction 3). Round 10 was merged (0071fef) and then
  // REJECTED, and its shape was the default through crestOpen 0.35, crestDashOpen 0.14 and
  // crestBandWeight 1. Each value below is the one that makes its switch the identity, by the
  // arithmetic in sea.ts: `crestCoverageAt` multiplies by exactly 1 at crestOpen 1,
  // `crestFieldAt` returns the open field untouched at weight 0, and `crestDashOnAt` is
  // CREST_DASH_ON at every shoreness when the open fraction equals it. The arithmetic is not the
  // proof: tools/lanes/surfaces/sea-same.mjs captured this arm and the tree before the merge
  // (802e86d) at one pinned save, camera, instant and water tick, and compared pixel arrays
  // (gauntlet/rounds/living-sea-r11/step0/same.json). The switches stay, so round 10's shape is
  // `?crestopen=0.35&crestdashopen=0.14&crestband=1` away for anyone who wants to see it again.
  crestOpen: 1, // round 10 shipped 0.35. 1 is the identity: open water keeps the calm's own coverage
  crestBandPx: 8, // GUESS: a mark is about 8 art px across the shore, so a contour lands every 8 to 16 px out from a coast. The references' bright blobs are 2.0, 2.9 and 10.4 art px thick
  crestDashOpen: 0.45, // round 10 shipped 0.14. This is sea.ts CREST_DASH_ON, restated because water.ts cannot import sea.ts; sea.test.ts fences the two equal
  crestBandWeight: 0, // round 10 shipped 1. 0 is the identity: the crest field is the open sea's field everywhere
  crestLift: 1, // DEFAULTS TO TODAY, deliberately: the round that added this switch did not also move it. palette.ts CREST_LIFT_SCALE is the same 1 and palette.test.ts fences them equal
  crestStrokePx: 2, // DEFAULTS TO TODAY: sea.ts CREST_STROKE_PX, restated here because water.ts cannot import sea.ts (sea.ts imports this file). sea.test.ts fences the two equal
  foamPx: 9, // GUESS, and it is the band's own depth; see contact.ts on why it is 9 and not 10
};

/**
 * THE MARGIN A SHELF MAY BE CULLED AT, and it is NOT the shore's.
 *
 * A shelf reaches `shelfPx` (96) out from a silhouette, and the tone field looks the shelf up at
 * the WARPED point, which is up to `warpAmp` (40) px away from the true one. MEASURED by a critic
 * on the shipped tree: culled on the bare box, the draw a sprite's box crossed the frame edge
 * moved the tone band by a median of 4 and a maximum of 5 of the 7 bands over an 80 px strip.
 * `WATER_CELL_PX` is the tone lattice's own cell, whose CORNERS are sampled and can sit outside
 * the frame while the cell is partly inside it.
 */
export function shelfCullMarginPx(sea: WaterSea): number {
  return sea.shelfPx + sea.warpAmp + WATER_CELL_PX;
}

/** The mode's query-parameter parse, so world-entry and every instrument agree on the spelling. */
export function waterModeOf(v: string | null | undefined): WaterMode {
  return v === 'settled' ? 'settled' : v === 'render' ? 'render' : WATER_MODE;
}
